import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Grid,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import axios from 'axios';

const ReviewerManager = () => {
  const [reviewers, setReviewers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentReviewer, setCurrentReviewer] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    role: 'reviewer' // Default role is reviewer
  });
  const [dialogMode, setDialogMode] = useState('add');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch reviewers on component mount
  useEffect(() => {
    fetchReviewers();
  }, []);

  // Fetch reviewers from API
  const fetchReviewers = async () => {
    setLoading(true);
    try {
      // Use the dedicated reviewers endpoint for managers
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/audit/reviewers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setReviewers(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching reviewers:', err);
      setError('Failed to load reviewers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle dialog open for adding a new reviewer
  const handleAddReviewer = () => {
    setCurrentReviewer({
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
      role: 'reviewer'
    });
    setDialogMode('add');
    setOpenDialog(true);
  };

  // Handle dialog open for editing a reviewer
  const handleEditReviewer = (reviewer) => {
    setCurrentReviewer({
      id: reviewer.id,
      name: reviewer.name,
      email: reviewer.email,
      password: '',
      password_confirmation: '',
      role: 'reviewer'
    });
    setDialogMode('edit');
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentReviewer({
      ...currentReviewer,
      [name]: value
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password confirmation
    if (currentReviewer.password !== currentReviewer.password_confirmation) {
      setSnackbar({
        open: true,
        message: 'Passwords do not match',
        severity: 'error'
      });
      return;
    }
    
    try {
      let response;
      
      if (dialogMode === 'add') {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/audit/reviewers`, currentReviewer, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        setSnackbar({
          open: true,
          message: 'Reviewer added successfully',
          severity: 'success'
        });
      } else {
        // For edit, only send password if it's not empty
        const reviewerData = { ...currentReviewer };
        if (!reviewerData.password) {
          delete reviewerData.password;
          delete reviewerData.password_confirmation;
        }
        
        response = await axios.put(`${process.env.REACT_APP_API_URL}/audit/reviewers/${currentReviewer.id}`, reviewerData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        setSnackbar({
          open: true,
          message: 'Reviewer updated successfully',
          severity: 'success'
        });
      }
      
      handleCloseDialog();
      fetchReviewers();
    } catch (err) {
      console.error('Error saving reviewer:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to save reviewer',
        severity: 'error'
      });
    }
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Reviewer Management</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={handleAddReviewer}
        >
          Add Reviewer
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviewers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No reviewers found. Add your first reviewer!
                  </TableCell>
                </TableRow>
              ) : (
                reviewers.map((reviewer) => (
                  <TableRow key={reviewer.id}>
                    <TableCell>{reviewer.name}</TableCell>
                    <TableCell>{reviewer.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label="Reviewer" 
                        color="primary" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEditReviewer(reviewer)}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Add/Edit Reviewer Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add New Reviewer' : 'Edit Reviewer'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Name"
              name="name"
              value={currentReviewer.name}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              type="email"
              value={currentReviewer.email}
              onChange={handleInputChange}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="password"
                  label={dialogMode === 'add' ? 'Password' : 'New Password (leave blank to keep current)'}
                  name="password"
                  type="password"
                  value={currentReviewer.password}
                  onChange={handleInputChange}
                  required={dialogMode === 'add'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="password_confirmation"
                  label="Confirm Password"
                  name="password_confirmation"
                  type="password"
                  value={currentReviewer.password_confirmation}
                  onChange={handleInputChange}
                  required={dialogMode === 'add' || currentReviewer.password !== ''}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {dialogMode === 'add' ? 'Add Reviewer' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewerManager;
